// Fund data-sources sub-route — /fund/:fundId/accounts (16.d.4.2).
// Second step of the 16.d.4 mega-page split. The accounts/wallets the fund
// tracks, plus the "+ Add a data source" flow (15.1.b: attach a position the
// fund already holds to a LIVE fund, funding=internal so it's net-zero to NAV
// and not refused as new external capital). Self-loads via GET
// /ops/funds/{id}/accounts using fundId from the URL — no shared fund context
// yet (the FundLayout + subnav lands in 16.d.4.5). Manager-gated in App.tsx.
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { PageHeader } from '../../components/shell';
import { CredentialRow, type CredentialAccount } from '../../components/CredentialRow';
import { api } from '../../lib/api';
import { AddDataSourceForm } from './FundOnboard';

type CredMeta = { has_credentials: boolean; key_last_four: string | null; credential_status: string | null };

const CHAIN_LABELS: Record<string, string> = { ethereum: 'Ethereum', bsc: 'BNB Smart Chain' };
const venueLabel = (venue: string, chain?: string) => {
  if (venue === 'coinbase_exchange') return 'Coinbase Exchange';
  if (venue === 'bybit') return 'Bybit';
  if (venue === 'kraken') return 'Kraken';
  if (venue === 'onchain') return chain ? `On-chain · ${CHAIN_LABELS[chain] ?? chain}` : 'On-chain';
  return venue;
};

export default function FundAccounts() {
  const { fundId } = useParams<{ fundId: string }>();
  const [accounts, setAccounts] = useState<any[] | null>(null);
  const [credMeta, setCredMeta] = useState<Record<string, CredMeta>>({});
  const [credErr, setCredErr] = useState<Set<string>>(new Set());
  const [showAddSource, setShowAddSource] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // 16.e.4 — load accounts + per-CEX credential metadata on the LIVE fund page
  // (the fund-accounts GET omits credential meta; mirror FundOnboard's 14.a.4c.4
  // fetch, INCLUDING its `cancelled` staleness guard and `credErr` failed-fetch
  // fallback, so a cross-fund nav can't show stale data and a first-load fetch
  // failure shows a retry hint rather than a permanent "LOADING…").
  useEffect(() => {
    if (!fundId) return;
    let cancelled = false;
    setErr(null);
    api<any[]>(`/ops/funds/${fundId}/accounts`)
      .then(async rows => {
        if (cancelled) return;
        setAccounts(rows);
        const cex = rows.filter(a => a.venue === 'coinbase_exchange');
        const metas = await Promise.all(cex.map(a =>
          api<CredMeta>(`/ops/accounts/${a.id}/credentials`)
            .then(m => [a.id, { has_credentials: m.has_credentials, key_last_four: m.key_last_four, credential_status: m.credential_status ?? null }] as const)
            .catch(() => [a.id, null] as const)));
        if (cancelled) return;
        const ok: Record<string, CredMeta> = {};
        const failed = new Set<string>();
        for (const [id, m] of metas) { if (m) ok[id] = m; else failed.add(id); }
        // Merge (don't reset) so a transient re-fetch failure doesn't flash a row to NOT CONNECTED.
        setCredMeta(prev => ({ ...prev, ...ok }));
        setCredErr(failed);
      })
      .catch(() => { if (!cancelled) setErr('Failed to load data sources.'); });
    return () => { cancelled = true; };
  }, [fundId, reloadKey]);

  if (!fundId) return null;

  return (
    <>
      <PageHeader
        kicker="FUND"
        title={<>Data <em>sources.</em></>}
      />
      {err && <p className="serif-body" style={{ color: 'var(--oxblood)', marginTop: 12 }}>{err}</p>}

      <section style={{ marginTop: 24 }}>
        <div className="kicker-row">
          <span className="kicker">DATA SOURCES</span>
          <button type="button" className="btn-link" onClick={() => setShowAddSource(v => !v)}>
            {showAddSource ? '− Cancel' : '+ Add a data source'}
          </button>
        </div>
        <table className="tbl" style={{ marginTop: 12 }}>
          <thead><tr><th>SOURCE</th><th>VENUE</th><th data-align="right">STATUS</th></tr></thead>
          <tbody>
            {(accounts || []).map(a => (
              <tr key={a.id}>
                <td>{a.nickname || '—'}</td>
                <td>{venueLabel(a.venue, a.chain)}</td>
                <td data-align="right" className="label-s">{String(a.status || '').toUpperCase()}</td>
              </tr>
            ))}
            {accounts && !accounts.length && <tr><td colSpan={3} className="label-s" style={{ color: 'var(--ink-3)' }}>No data sources attached yet.</td></tr>}
          </tbody>
        </table>
        {showAddSource && (
          <div style={{ marginTop: 24 }}>
            <p className="serif-body" style={{ fontSize: 14, fontStyle: 'italic', color: 'var(--ink-2)', marginBottom: 16 }}>
              Add a position this fund already holds, funded from the fund's own capital — it is net-zero to NAV. New external capital must come in through a subscription.
            </p>
            <AddDataSourceForm fundId={fundId} funding="internal"
                               onAdded={() => { setShowAddSource(false); setReloadKey(k => k + 1); }} />
          </div>
        )}
      </section>

      {(accounts || []).some(a => a.venue === 'coinbase_exchange') && (
        <section style={{ marginTop: 48 }}>
          <span className="kicker">EXCHANGE CONNECTIONS</span>
          <p className="serif-body" style={{ fontSize: 14, fontStyle: 'italic', color: 'var(--ink-2)', margin: '8px 0 16px', maxWidth: '60ch' }}>
            Connect, rotate, test, or revoke each exchange account's view-only API key.
          </p>
          {(accounts || []).filter(a => a.venue === 'coinbase_exchange').map(a => {
            const m = credMeta[a.id];
            if (!m) return (
              <p key={a.id} className="label-s"
                 style={{ color: credErr.has(a.id) ? 'var(--oxblood)' : 'var(--ink-3)', marginTop: 8 }}>
                {(a.nickname || 'Coinbase Exchange').toUpperCase()} — {credErr.has(a.id)
                  ? 'CONNECTION STATUS UNAVAILABLE — RELOAD TO RETRY' : 'LOADING…'}
              </p>
            );
            const acct: CredentialAccount = {
              id: a.id, label: a.nickname || venueLabel(a.venue), venue: a.venue,
              has_credentials: m.has_credentials, key_last_four: m.key_last_four,
              credential_status: m.credential_status,
            };
            return <CredentialRow key={a.id} account={acct} onChange={() => setReloadKey(k => k + 1)} />;
          })}
        </section>
      )}
    </>
  );
}
